import { useQuery } from '@tanstack/react-query';
import { useContractQuery } from './useContractQuery';
import { useReadContracts } from './useContracts';
import { useRequestCount } from './useAuditingPoolConfig';

/* -------------------------------------------------------------- *
 *   Milestone state machine
 * -------------------------------------------------------------- */

export const MILESTONE_STATES = [
  'Locked',
  'Unlocked',
  'Claimed',
  'ProofSubmitted',
  'Disputed',
  'Approved',
] as const;

export type MilestoneState = (typeof MILESTONE_STATES)[number];

/** Translate the 0..5 enum value returned by getMilestoneStatus(). */
export function milestoneStateName(value: number | bigint | undefined): MilestoneState | null {
  if (value === undefined) return null;
  const i = Number(value);
  return (MILESTONE_STATES[i] ?? null) as MilestoneState | null;
}

/* -------------------------------------------------------------- *
 *   Deliverable types (§3.2)
 * -------------------------------------------------------------- */

export const DELIVERABLE_TYPES = [
  'StudyReport',
  'DataPackage',
  'IPAssignment',
  'Prototype',
  'TechTransfer',
  'Other',
] as const;
export type DeliverableType = (typeof DELIVERABLE_TYPES)[number];

export function deliverableTypeName(v: number | bigint | undefined): DeliverableType | null {
  if (v === undefined) return null;
  return (DELIVERABLE_TYPES[Number(v)] ?? null) as DeliverableType | null;
}

export type DeliverableRow = {
  dtype: bigint;          // DeliverableType enum index
  evidenceHash: string;   // bytes32
  uri: string;
};

/* -------------------------------------------------------------- *
 *   Milestone schedule + per-milestone queries
 * -------------------------------------------------------------- */

/** approvedMilestoneNames[], approvedMilestoneAmounts[] (after verdict). */
export function useApprovedMilestones(id: number | bigint | null | undefined) {
  type Milestones = [string[], bigint[]];
  return useContractQuery<Milestones>(
    ['auditingPool', 'getApprovedMilestones', BigInt(id ?? 0)],
    { enabled: id !== null && id !== undefined }
  );
}

/** proposedMilestoneNames[], proposedMilestoneAmounts[] (lab's original ask). */
export function useProposedMilestones(id: number | bigint | null | undefined) {
  type Milestones = [string[], bigint[]];
  return useContractQuery<Milestones>(
    ['auditingPool', 'getProposedMilestones', BigInt(id ?? 0)],
    { enabled: id !== null && id !== undefined }
  );
}

/**
 * v3 (§4.8) — full milestone schedule in one call: names + amounts +
 * statuses arrays. Pre-verdict returns the proposed schedule; post-verdict
 * the approved one. Saves the N+1 fan-out from before.
 */
export function useMilestoneSchedule(id: number | bigint | null | undefined) {
  type Schedule = {
    names: string[];
    amounts: bigint[];
    statuses: bigint[];   // MilestoneState enum indices
  };
  return useContractQuery<Schedule>(
    ['auditingPool', 'getMilestoneSchedule', BigInt(id ?? 0)],
    { enabled: id !== null && id !== undefined }
  );
}

/** Per-milestone state machine value (0 Locked .. 5 Approved). */
export function useMilestoneStatus(
  reqId: number | bigint | null | undefined,
  milestoneIdx: number | bigint
) {
  return useContractQuery<bigint>(
    ['auditingPool', 'getMilestoneStatus', BigInt(reqId ?? 0), BigInt(milestoneIdx)],
    { enabled: reqId !== null && reqId !== undefined }
  );
}

/**
 * v3 (§3.1) — review-progress for a milestone: how many approves, how
 * many rejects, total auditors on the request. Crosses-threshold when
 * approveCount * 2 > totalAuditors.
 */
export function useMilestoneReviewProgress(
  reqId: number | bigint | null | undefined,
  milestoneIdx: number | bigint
) {
  type Progress = {
    approveCount: bigint;
    rejectCount: bigint;
    totalAuditors: bigint;
  };
  return useContractQuery<Progress>(
    ['auditingPool', 'getMilestoneReviewProgress', BigInt(reqId ?? 0), BigInt(milestoneIdx)],
    { enabled: reqId !== null && reqId !== undefined }
  );
}

/** v3 (§3.2) — typed deliverable bundle for a milestone. Empty if lab used legacy bytes32 proof. */
export function useMilestoneDeliverables(
  reqId: number | bigint | null | undefined,
  milestoneIdx: number | bigint
) {
  return useContractQuery<DeliverableRow[]>(
    ['auditingPool', 'getMilestoneDeliverables', BigInt(reqId ?? 0), BigInt(milestoneIdx)],
    { enabled: reqId !== null && reqId !== undefined }
  );
}

/** v3 (§3.3) — MilestoneType for the i-th milestone in a typed-schedule request. */
export function useRequestMilestoneType(
  reqId: number | bigint | null | undefined,
  milestoneIdx: number | bigint
) {
  return useContractQuery<bigint>(
    ['auditingPool', 'requestMilestoneTypes', BigInt(reqId ?? 0), BigInt(milestoneIdx)],
    { enabled: reqId !== null && reqId !== undefined }
  );
}

/** v3 — is the given milestone DSMB-vetoed right now? */
export function useMilestoneDsmbVetoed(
  reqId: number | bigint | null | undefined,
  milestoneIdx: number | bigint
) {
  return useContractQuery<boolean>(
    ['auditingPool', 'milestoneDsmbVetoed', BigInt(reqId ?? 0), BigInt(milestoneIdx)],
    { enabled: reqId !== null && reqId !== undefined }
  );
}

/** v3 — which firm vetoed milestone N? (zero address if no veto active.) */
export function useMilestoneDsmbVetoBy(
  reqId: number | bigint | null | undefined,
  milestoneIdx: number | bigint
) {
  return useContractQuery<string>(
    ['auditingPool', 'milestoneDsmbVetoBy', BigInt(reqId ?? 0), BigInt(milestoneIdx)],
    { enabled: reqId !== null && reqId !== undefined }
  );
}

/** bytes32 proof anchor for the current milestone. With v3 submitDeliverables this is keccak256(abi.encode(deliverables)). */
export function useStageProof(id: number | bigint | null | undefined) {
  return useContractQuery<string>(
    ['auditingPool', 'getStageProof', BigInt(id ?? 0)],
    { enabled: id !== null && id !== undefined }
  );
}

/* -------------------------------------------------------------- *
 *   §3.3 option-exercise queue (#5 admin panel)
 *
 *   For requests submitted with optionExerciseRequired=true, every
 *   milestone N≥1 stays Locked after milestone N-1 is Approved until
 *   the Program Officer (contract owner — typically OpenGovernance)
 *   calls exerciseOption(reqId, N). This hook surfaces the queue of
 *   milestones currently in that Locked state so an admin UI can
 *   render them with an "Exercise" button per row.
 * -------------------------------------------------------------- */

export type ExerciseQueueRow = {
  requestId: number;
  milestoneIdx: number;
  title: string;
  milestoneName: string;
  amountWei: bigint;
};

/**
 * Walks every accepted+non-cancelled request that has optionExerciseRequired
 * set, and returns the list of milestones that are currently in the Locked
 * state (and therefore waiting for exerciseOption). Skips milestone 0
 * because it's auto-unlocked at acceptVerdict (BARDA base-period analogue).
 *
 * Cost: 2 RPC calls per request (requestList + getMilestoneSchedule). With
 * <100 requests on Sepolia this is fine; for mainnet scale we'd cache via
 * an indexer.
 */
export function useExerciseQueue() {
  const contracts = useReadContracts();
  const { data: countBn } = useRequestCount();
  const N = countBn !== undefined ? Number(countBn) : 0;

  return useQuery<ExerciseQueueRow[], Error>({
    queryKey: ['metha', 'auditingPool', 'exerciseQueue', N] as const,
    queryFn: async () => {
      if (N === 0) return [];
      const rows: ExerciseQueueRow[] = [];
      const requests = await Promise.all(
        Array.from({ length: N }, (_, i) => contracts.auditingPool.requestList(BigInt(i)))
      );
      const schedules = await Promise.all(
        Array.from({ length: N }, (_, i) => contracts.auditingPool.getMilestoneSchedule(BigInt(i)))
      );
      for (let i = 0; i < N; i++) {
        const r = requests[i];
        // requestList returns a tuple; key fields by index since the named
        // properties depend on ABI compilation. Indexes match the struct:
        //   0:title 1:description 2:ipfsHash 3:labWallet 4:auditors
        //   5:proposedMilestoneNames 6:proposedMilestoneAmounts
        //   7:approvedMilestoneAmounts 8:currentMilestone 9:milestoneStatus
        //   10:approveVotes 11:totalVotes 12:hype 13:verdictCast
        //   14:verdictAccepted 15:cancelled 16:optionExerciseRequired
        const title = (r.title ?? r[0]) as string;
        const verdictAccepted = (r.verdictAccepted ?? r[14]) as boolean;
        const cancelled = (r.cancelled ?? r[15]) as boolean;
        const optionRequired = (r.optionExerciseRequired ?? r[16]) as boolean;
        if (!optionRequired || !verdictAccepted || cancelled) continue;

        const sched = schedules[i];
        const names = (sched.names ?? sched[0]) as string[];
        const amounts = (sched.amounts ?? sched[1]) as bigint[];
        const statuses = (sched.statuses ?? sched[2]) as bigint[];
        for (let m = 1; m < statuses.length; m++) {
          // Status 0 = Locked. Skip m=0 because that's auto-unlocked at acceptVerdict.
          if (Number(statuses[m]) === 0) {
            rows.push({
              requestId: i,
              milestoneIdx: m,
              title,
              milestoneName: names[m] ?? `Milestone ${m + 1}`,
              amountWei: amounts[m] ?? 0n,
            });
          }
        }
      }
      return rows;
    },
    enabled: N >= 0,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}
